import { Form, Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Cards, FormField, Text } from '@components';
import { getWAFConditionsColumns } from '@constants';
import {
  columnDirection,
  columnSpace,
  ConditionOperatorType,
  ConditionVariableType,
  Dictionaries,
  InputType,
  StatusType,
  Translation,
  VariantType,
  WAFActionType,
  WAFRulesType,
} from '@enums';
import { useAppDispatch } from '@hooks';
import { addSnack } from '@store';
import { getOptionListFromCatalog } from '@utils';

export const CustomWAFRules = () => {
  const { t: tButton } = useTranslation(Translation.Common, { keyPrefix: 'button' });
  const { t: tForm } = useTranslation(Translation.Configuration, { keyPrefix: 'forms.waf.custom' });
  const { t: tToast } = useTranslation(Translation.Configuration, { keyPrefix: 'toasts.waf' });
  const { t: tPredefinedForm } = useTranslation(Translation.Configuration, { keyPrefix: 'forms.waf.predefined' });

  const CustomWafData: any = {};

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const segments = location.pathname.split('/');
  const backtoWAF = segments.slice(0, -1).join('/');
  const hasCustomWafData = Object.keys(CustomWafData).length > 0;
  const handleSubmit = (values) => {
    navigate(backtoWAF);
    dispatch(
      addSnack({
        type: StatusType.Success,
        message: hasCustomWafData ? tToast('createWAFSuccessful') : tToast('editWAFSuccessful'),
      }),
    );
  };

  const WAFConditionTableColumns = useMemo(getWAFConditionsColumns, []);

  const defaultFormFieldProps = {
    direction: columnDirection.Row,
    columnPercentage: columnSpace.Half,
    fullWidth: true,
    required: true,
  };

  return (
    <div className="p-1">
      <Cards className="flex flex-1 flex-col w-full" rounded="rounded-2xl">
        <Formik
          initialValues={{
            name: CustomWafData?.name ?? '',
            blockAction: CustomWafData?.blockAction ?? WAFActionType.Log,
            wafRules: CustomWafData?.wafRules ?? [{ value: WAFRulesType.scanner_detection }],
            conditions: CustomWafData?.conditions ?? [],
            crossRequestMode: CustomWafData?.crossRequestMode ?? false,
          }}
          onSubmit={handleSubmit}
        >
          {({ values, touched, errors }) => (
            <Form className="bg-white rounded-lg py-3 space-y-8">
              <Text $level={2} $customizeColor className="text-gray-700">
                {tForm(hasCustomWafData ? 'editWaf' : 'createNewWaf')}
              </Text>
              <div className="grid grid-cols-1 gap-y-6 pt-3">
                <FormField
                  {...defaultFormFieldProps}
                  label={tForm('customWafRulesName')}
                  name="name"
                  placeholder={tForm('enterYourCustomWafRulesName')}
                  isCorrect={touched.name && !errors.name}
                />

                <FormField
                  {...defaultFormFieldProps}
                  direction={columnDirection.Column}
                  inputType={InputType.Table}
                  withoutLabel
                  label={tForm('conditions')}
                  columns={WAFConditionTableColumns}
                  name="conditions"
                  placeholder={''}
                  emptyData={{
                    main: {
                      field: ConditionVariableType.URI,
                      argument: '',
                      operator: ConditionOperatorType.stringEqual,
                      values: [{ value: '' }],
                    },
                  }}
                />
                <FormField
                  {...defaultFormFieldProps}
                  inputType={InputType.Radio}
                  label={tForm('wafRules')}
                  columns={WAFConditionTableColumns}
                  direction={columnDirection.Row}
                  name="wafRules"
                  placeholder={''}
                />

                <FormField
                  {...defaultFormFieldProps}
                  label={tForm('blockAction')}
                  direction={columnDirection.Row}
                  name="blockAction"
                  inputType={InputType.Select}
                  placeholder={tForm('selectYourBlockAction')}
                  options={getOptionListFromCatalog({ name: Dictionaries.WAFAction })}
                />
              </div>
              <FormField
                {...defaultFormFieldProps}
                inputType={InputType.Switch}
                label={tPredefinedForm('crossRequestMode')}
                name="crossRequestMode"
                placeholder={''}
                description={tPredefinedForm('crossRequestHints')}
              />
              <div className="sm:grid sm:grid-cols-2 gap-8">
                <Button
                  variant={VariantType.Outlined}
                  onClick={() => {
                    navigate(backtoWAF);
                  }}
                  className="rounded-md ring-theme-primary-main"
                  adornment={
                    <Text $level={6} className="ellipsis-text">
                      {tButton('cancel')}
                    </Text>
                  }
                />
                <Button type="submit">{tButton(CustomWafData ? 'save' : 'create')}</Button>
              </div>
            </Form>
          )}
        </Formik>
      </Cards>
    </div>
  );
};
